import { useEffect, useRef, useState, CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/lib/supabase';
import { useError } from '@/hooks/useError';
import type { Tables } from '@/shared/database';
import styles from './Work.module.scss';

const INITIAL_COUNT = 4;
const LOAD_BATCH = 4;
const CARD_VISIBILITY_THRESHOLD = 0.15;

type ProjectWithTechs = Tables<'projects'> & {
  project_technologies: { technology: Pick<Tables<'technologies'>, 'name'> | null }[];
};

const Work = () => {
  const { t, i18n } = useTranslation();
  const { pushError } = useError();
  const gridRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const [visibleCount, setVisibleCount] = useState(INITIAL_COUNT);
  const [projects, setProjects] = useState<ProjectWithTechs[]>([]);

  useEffect(() => {
    supabase
      .from('projects')
      .select('*, project_technologies(technology:technologies(name))')
      .order('order', { ascending: true })
      .then(({ data, error }) => {
        if (error) {
          pushError(t('error.projectsFetchFailed'));
          return;
        }
        if (data) setProjects(data as ProjectWithTechs[]);
      });
  }, [pushError, t]);

  const visibleProjects = projects.slice(0, visibleCount);
  const remaining = Math.min(projects.length - visibleCount, LOAD_BATCH);
  const gradientSteps = visibleProjects.length - 1;

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          entry.target.classList.toggle(styles.visible, entry.isIntersecting);
        });
      },
      { threshold: CARD_VISIBILITY_THRESHOLD }
    );
    return () => observerRef.current?.disconnect();
  }, []);

  useEffect(() => {
    const grid = gridRef.current;
    const observer = observerRef.current;
    if (!grid || !observer) return;
    Array.from(grid.children).forEach((card) => observer.observe(card));
  }, [visibleCount, projects]);

  return (
    <div className={styles.work}>
      <div className={styles.grid} ref={gridRef}>
        {visibleProjects.map(
          ({ id, name, description_en, description_sk, role_en, role_sk, project_technologies }, index) => (
            <div
              key={id}
              className={styles.card}
              style={{ '--card-t': (gradientSteps > 0 ? index / gradientSteps : 0).toFixed(3) } as CSSProperties}
            >
              <h3 className={styles.cardTitle}>{name}</h3>
              <p className={styles.description}>{i18n.language === 'sk' ? description_sk : description_en}</p>
              <p className={styles.contribution}>{i18n.language === 'sk' ? role_sk : role_en}</p>
              <ul className={styles.stack}>
                {project_technologies
                  .filter((pt) => pt.technology !== null)
                  .map((pt) => (
                    <li key={pt.technology!.name} className={styles.tag}>
                      {pt.technology!.name}
                    </li>
                  ))}
              </ul>
            </div>
          )
        )}
      </div>
      {remaining > 0 && (
        <button type='button' className={styles.loadMoreBtn} onClick={() => setVisibleCount((c) => c + LOAD_BATCH)}>
          {t('work.loadMore', { count: remaining })}
        </button>
      )}
    </div>
  );
};

export default Work;
